/**
 * Formato estrito dos eventos do Diagnóstico. Nenhum texto livre é guardado:
 * mensagem = categoria conhecida; rota = segmentos da lista de rotas do app ou marcador;
 * build/correlação/alvo com formato controlado. O servidor (0232) aplica as mesmas regras.
 */
export const DIAG_KINDS = ["js_error", "unhandled_rejection", "react_boundary", "request_failed", "chunk_load"] as const;
export const DIAG_APPS = ["site", "painel", "outro"] as const;

/** Nomes nativos de erro aceitos; qualquer outro nome vira "Error". */
export const ERROR_NAMES = [
  "Error", "TypeError", "ReferenceError", "SyntaxError", "RangeError", "URIError", "EvalError", "AggregateError",
  "DOMException", "NotFoundError", "HierarchyRequestError", "InvalidStateError", "SecurityError", "QuotaExceededError",
  "NetworkError", "NotAllowedError", "InvalidCharacterError", "DataCloneError", "TimeoutError", "ChunkLoadError",
] as const;

/** Pistas categóricas detectadas na mensagem original (que é descartada). */
const HINTS: [string, RegExp][] = [
  ["chunk_load", /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i],
  ["dom_remove_child", /removeChild/],
  ["dom_insert_before", /insertBefore/],
  ["fetch_failed", /Failed to fetch|Load failed|NetworkError when attempting/i],
  ["null_property", /Cannot read propert(y|ies) of (null|undefined)|is null|is undefined|null is not an object|undefined is not an object/i],
  ["not_a_function", /is not a function/i],
  ["not_defined", /is not defined|Can't find variable/i],
  ["max_stack", /Maximum call stack/i],
  ["invalid_json", /JSON\.parse|Unexpected token .* JSON|is not valid JSON/i],
];
export const HINT_NAMES = HINTS.map(([h]) => h);

export const MESSAGE_RE = new RegExp(
  `^(?:(?:${[...ERROR_NAMES, "non_error"].join("|")})(?:/(?:${HINT_NAMES.join("|")}))?|HTTP [1-5][0-9]{2}|network_error)$`,
);
export const BUILD_RE = /^(?:index-[A-Za-z0-9_-]{6,20}\.js|dev)$/;
export const CORRELATION_RE = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/;
export const TARGET_RE = /^(?:(?:fn|rpc|rest|auth):[a-z0-9_-]{1,60}|storage|other)$/;

/** Segmentos estáticos das rotas do app (App.tsx). Qualquer outro segmento é redigido. */
export const ROUTE_SEGMENTS = [
  "achadinhos", "admin", "analise-produtos", "autoatendimento", "automacoes", "autorizacao", "avaliacoes", "avaliar",
  "banner-home", "banners-vitrine", "beneficios", "brindes", "busca", "caixas", "calculadora-frete", "calendario-loja",
  "campanhas", "cashback", "catalogo", "categoria", "categorias", "central-lancamentos", "checkout", "cliente", "clientes",
  "colaboradores", "colecao", "colecoes-inteligentes", "compras", "compre-o-look", "conferir-pix", "configuracoes",
  "confirmar", "conversao", "costureira", "cupons", "curadoria-arara", "descadastrar", "descontos-campanha",
  "desempenho-produtos", "devolucoes", "diagnostico", "dre", "dre-gerencial", "email-template", "entradas", "entrar",
  "enviar", "etiquetas", "expediente", "familia", "favoritos", "feedback", "feedbacks", "fotos", "gastos",
  "gastos-pendentes", "gestao-devolucoes", "grupos", "grupos-whatsapp", "historico", "imprimir-etiqueta", "inventario",
  "lancamentos", "lavagem", "layout-site", "live", "login", "looks", "metas", "meu-calendario", "meus-pedidos",
  "minhas-metas", "monitor", "movimentacoes", "newsletter", "notas-fiscais", "notificacoes", "nova", "novidades",
  "ordem-vitrine", "origem-vendas", "outlet-vip", "ouvidoria", "p", "pagar", "pagar-complemento", "painel", "pdv",
  "pedido", "pedido-manual", "pedidos", "pesquisar", "pix", "politica-cookies", "politica-de-privacidade",
  "politica-privacidade", "ponto", "produto", "produtos", "promocao-24h", "promocoes", "rastreio", "recuperar",
  "registros-ponto", "regras-quantidade", "reservas", "reset-password", "retomar", "revisao", "rfid", "rodizio", "saidas",
  "sale", "separacao", "separacao-tv", "servicos", "servicos-externos", "sessao", "solicitar-devolucao", "status",
  "suprimentos", "tarefas", "tela-pdv", "tela-promo", "tela-vitrine", "termos-de-uso", "tiktok-shop", "transferencias",
  "troca", "troca-facil", "trocas", "trocas-devolucoes", "tv", "unsubscribe", "usuarios", "vagas", "videos",
] as const;
const SEGMENTS = new Set<string>(ROUTE_SEGMENTS);
const MARKERS = new Set([":id", ":n", ":x"]);

/** Mensagem categórica: nunca devolve texto do erro. */
export function categorizeError(raw: unknown): string {
  let name = "non_error";
  let text = "";
  try {
    if (raw instanceof Error || (raw && typeof raw === "object" && "name" in (raw as object) && "message" in (raw as object))) {
      const n = String((raw as Error).name ?? "");
      name = (ERROR_NAMES as readonly string[]).includes(n) ? n : "Error";
      text = String((raw as Error).message ?? "");
    } else if (typeof raw === "string") {
      text = raw;
      const m = raw.match(/^(?:Uncaught )?([A-Za-z]+Error|DOMException)\b/);
      if (m && (ERROR_NAMES as readonly string[]).includes(m[1])) name = m[1];
    }
  } catch { /* fica non_error */ }
  const hint = HINTS.find(([, re]) => re.test(text))?.[0];
  return hint ? `${name}/${hint}` : name;
}

/** Rota sem query/hash: só segmentos conhecidos; número → :n, uuid → :id, resto → :x. */
export function safeRoute(path: string): string {
  const p = String(path || "/").split(/[?#]/)[0];
  const out = p.split("/").map((seg, i) => {
    if (i === 0 && seg === "") return "";
    if (seg === "") return "";
    if (SEGMENTS.has(seg) || MARKERS.has(seg)) return seg;
    if (/^\d+$/.test(seg)) return ":n";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return ":id";
    return ":x";
  }).join("/");
  return (out || "/").slice(0, 120);
}

export function isSafeRoute(r: unknown): r is string {
  return typeof r === "string" && r.length <= 120 && r.startsWith("/") && safeRoute(r) === r;
}

export type SafeEventShape = {
  kind: string; app: string; message: string; route: string; build: string | null; correlation_id: string;
  target?: string; http_status?: number; at?: string;
};

/** Validação completa (buffer local na leitura/exportação). */
export function isSafeEvent(e: unknown): e is SafeEventShape {
  if (!e || typeof e !== "object") return false;
  const x = e as Record<string, unknown>;
  const allowed = new Set(["kind", "app", "message", "route", "build", "correlation_id", "target", "http_status", "at"]);
  if (Object.keys(x).some((k) => !allowed.has(k))) return false;
  if (!(DIAG_KINDS as readonly string[]).includes(String(x.kind))) return false;
  if (!(DIAG_APPS as readonly string[]).includes(String(x.app))) return false;
  if (typeof x.message !== "string" || !MESSAGE_RE.test(x.message)) return false;
  if (!isSafeRoute(x.route)) return false;
  if (x.build !== null && (typeof x.build !== "string" || !BUILD_RE.test(x.build))) return false;
  if (typeof x.correlation_id !== "string" || !CORRELATION_RE.test(x.correlation_id)) return false;
  if (x.target !== undefined && (typeof x.target !== "string" || !TARGET_RE.test(x.target))) return false;
  if (x.http_status !== undefined && !(Number.isInteger(x.http_status) && (x.http_status as number) >= 0 && (x.http_status as number) <= 599)) return false;
  if (x.at !== undefined && (typeof x.at !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/.test(x.at))) return false;
  return true;
}

/** Para exibir/exportar mensagens vindas do servidor: fora do formato, redige. */
export function displayMessage(m: unknown): string {
  return typeof m === "string" && MESSAGE_RE.test(m) ? m : "[redigido]";
}
